import { zodResolver } from "@hookform/resolvers/zod";
import { MailCheck } from "lucide-react";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import AppLogo from "../../components/ui/AppLogo";
import { Spinner } from "../../components/ui/common";
import SplitScreenLayout from "../../components/views/layouts/SplitScreenLayout";
import { useToast } from "../../hooks/useToast";
import { useForgotPasswordMutation } from "./authApiSlice";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [sentTo, setSentTo] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onBlur",
    defaultValues: { email: "" },
  });

  const onSubmit = async ({ email }: ForgotPasswordFormData) => {
    try {
      await forgotPassword({ email }).unwrap();
      setSentTo(email);
    } catch (error: any) {
      toast.error({
        title: "Request Failed",
        message: getErrorDetails(error).message,
      });
    }
  };

  return (
    <SplitScreenLayout>
      <div className="w-full max-w-md sm:max-w-lg">
        <div className="flex flex-col items-center mb-8">
          <AppLogo />
          <h1 className="text-primary font-semibold text-xl sm:text-2xl mt-4">
            Forgot password?
          </h1>
          <p className="text-sm sm:text-base text-pColor text-center mt-2">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        {sentTo ? (
          <div className="flex flex-col items-center text-center space-y-4">
            <MailCheck className="w-12 h-12 text-primary" />
            <p className="text-hColor">
              If an account exists for <strong>{sentTo}</strong>, a reset link
              has been sent. Please check your inbox.
            </p>
            <AppButton
              onClick={() => navigate("/sign-in")}
              className="w-full h-11 sm:h-12"
            >
              Back to Sign In
            </AppButton>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <AppInputField
                  label="Email"
                  type="email"
                  error={errors.email?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />
            <AppButton
              type="submit"
              disabled={isLoading}
              className="w-full h-11 sm:h-12 text-base font-medium"
            >
              {isLoading ? <Spinner /> : "Send Reset Link"}
            </AppButton>
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                onClick={() => navigate("/sign-in")}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}
      </div>
    </SplitScreenLayout>
  );
};

export default ForgotPassword;
